import type { MarketVariants } from '@/lib/i18n'

export type Slide4TooGoodCopy = {
  heading: string
  intro: string
  rule: string
  ctaLabel: string
  storyLabel: string
  storyTitle: string
  storySubtitle: string
  storyP1: string
  // Inline currency interpolation: scammer fees / deposit amounts.
  storyP2Before: string
  storyP2Deposit: string
  storyP2After: string
  storyP3Before: string
  storyP3Lost: string
  storyP3After: string
  callout1Heading: string
  callout1Body: string
  callout2Heading: string
  callout2Body: string
}

// Canonical US copy. GB / AU / CA override the currency-bearing fragments and
// the financial-regulator wording. Other strings auto-spell via useSlideCopy
// (e.g. "judgment" → "judgement" for GB / AU).
export const copy: MarketVariants<Slide4TooGoodCopy> = {
  US: {
    heading: 'Too-good-to-be-true offers',
    intro:
      'Guaranteed investment returns. Lottery wins you didn’t enter. Exclusive opportunities that won’t last. These scams work because hope is just as powerful as fear at clouding your judgment.',
    rule: 'The more exciting it feels, the more carefully you should check.',
    ctaLabel: 'Read James’s story',
    storyLabel: 'Real story',
    storyTitle: 'The Crypto Trap',
    storySubtitle: 'A polished website. A total lie.',
    storyP1:
      'James was added to a WhatsApp group full of people sharing screenshots of their investment returns. Daily profits. Weekly withdrawals. Everyone seemed to be doing well.',
    storyP2Before:
      'A group admin shared a link to a trading platform. It looked professional, with real-time charts and a personal dashboard. James deposited ',
    storyP2Deposit: '$1,000',
    storyP2After:
      '. Within days, his balance had doubled. Encouraged, he added more. This continued for weeks.',
    storyP3Before:
      'When he tried to withdraw, the platform asked for a "processing fee." Then a "conversion fee." Then a "minimum balance" charge. None of it unlocked his funds. By the time James realized the platform, the group, and every member in it were fake, he had lost over ',
    storyP3Lost: '$55,000',
    storyP3After: '.',
    callout1Heading: 'Why it worked',
    callout1Body:
      'Opportunity scams work because they feel like good news. Whether you found it yourself or it landed in your inbox, the excitement of a win makes you stop asking the questions that would protect you.',
    callout2Heading: 'How to protect yourself',
    callout2Body:
      'Before investing in anything, search the platform name online and check it against your country’s financial regulator (the SEC or FINRA in the US). If you can’t find it listed, don’t deposit.',
  },
  GB: {
    storyP2Deposit: '£1,000',
    storyP3Lost: '£55,000',
    callout2Body:
      'Before investing in anything, search the platform name online and check it against the FCA register at register.fca.org.uk. If you can’t find it listed, don’t deposit.',
  },
  CA: {
    storyP2Deposit: '$1,000',
    storyP3Lost: '$55,000',
    callout2Body:
      'Before investing in anything, search the platform name online and check it against the CSA National Registration Search at aretheyregistered.ca. If you can’t find it listed, don’t deposit.',
  },
  AU: {
    storyP2Deposit: '$1,000',
    storyP3Lost: '$55,000',
    callout2Body:
      'Before investing in anything, search the platform name online and check it against ASIC’s Moneysmart investor warnings at moneysmart.gov.au. If you can’t find it listed, don’t deposit.',
  },
  // Qatar face. No illustration ties to the name, so James becomes Yousef.
  // Riyals, and the two local regulators in place of the SEC / FINRA.
  QA: {
    ctaLabel: 'Read Yousef’s story',
    storyP1:
      'Yousef was added to a WhatsApp group full of people sharing screenshots of their investment returns. Daily profits. Weekly withdrawals. Everyone seemed to be doing well.',
    storyP2Before:
      'A group admin shared a link to a trading platform. It looked professional, with real-time charts and a personal dashboard. Yousef deposited ',
    storyP2Deposit: 'QAR 4,000',
    storyP3Before:
      'When he tried to withdraw, the platform asked for a "processing fee." Then a "conversion fee." Then a "minimum balance" charge. None of it unlocked his funds. By the time Yousef realized the platform, the group, and every member in it were fake, he had lost over ',
    storyP3Lost: 'QAR 200,000',
    callout2Body:
      'Before investing in anything, search the platform name online and check whether it is licensed by the Qatar Central Bank (QCB) or the Qatar Financial Markets Authority (QFMA). If you can’t find it listed, don’t deposit.',
  },
  ar: {
    heading: 'عروض أفضل من أن تكون حقيقية',
    intro:
      'عوائد استثمارية مضمونة. جوائز يانصيب لم تشارك فيها. فرص حصرية لن تدوم طويلاً. تنجح هذه العمليات لأن الأمل لا يقل قوة عن الخوف في التشويش على حكمك على الأمور.',
    rule: 'كلما بدا العرض أكثر إثارة، وجب عليك التحقق منه بعناية أكبر.',
    ctaLabel: 'اقرأ قصة يوسف',
    storyLabel: 'قصة حقيقية',
    storyTitle: 'فخ العملات الرقمية',
    storySubtitle: 'موقع أنيق. وكذبة كاملة.',
    storyP1:
      'أُضيف يوسف إلى مجموعة على واتساب مليئة بأشخاص يتشاركون لقطات شاشة لعوائد استثماراتهم. أرباح يومية. سحوبات أسبوعية. بدا أن الجميع يحقق نجاحاً.',
    storyP2Before:
      'شارك أحد مشرفي المجموعة رابطاً لمنصة تداول. بدت احترافية، برسوم بيانية مباشرة ولوحة تحكم شخصية. أودع يوسف ',
    storyP2Deposit: '4,000 ريال قطري',
    storyP2After:
      '. وخلال أيام تضاعف رصيده. وبدافع التشجيع أضاف المزيد. واستمر ذلك لأسابيع.',
    storyP3Before:
      'وعندما حاول السحب، طلبت المنصة "رسوم معالجة". ثم "رسوم تحويل". ثم رسوم "الحد الأدنى للرصيد". ولم يفرج أيٌّ منها عن أمواله. وحين أدرك يوسف أن المنصة والمجموعة وكل عضو فيها كانوا مزيّفين، كان قد خسر أكثر من ',
    storyP3Lost: '200,000 ريال قطري',
    storyP3After: '.',
    callout1Heading: 'لماذا نجحت الخدعة',
    callout1Body:
      'تنجح عمليات احتيال "الفرص" لأنها تبدو كخبر سار. وسواء عثرت عليها بنفسك أو وصلتك في رسالة، فإن حماسة الفوز تجعلك تتوقف عن طرح الأسئلة التي كانت ستحميك.',
    callout2Heading: 'كيف تحمي نفسك',
    callout2Body:
      'قبل أن تستثمر في أي شيء، ابحث عن اسم المنصة على الإنترنت وتحقّق مما إذا كانت مرخّصة من مصرف قطر المركزي أو هيئة قطر للأسواق المالية. إذا لم تجدها مدرجة، فلا تودع أموالك.',
  },
}
